import {
    Autocomplete,
    AutocompleteCloseReason,
    AutocompleteRenderGroupParams,
    AutocompleteRenderInputParams,
    Box,
    ClickAwayListener,
    FilterOptionsState,
    InputBase,
    Link,
    Popper,
    Typography,
    autocompleteClasses,
    lighten,
    styled,
    useTheme,
} from "@mui/material";
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MirageTheme } from "@mirage/theme/MuiMirage.module";
import { IFolderPath } from "@shared/mirage/core/contracts/IFoldersService";
import MirageFormControl from "../regularField/MirageFormControl";
import { useId } from "react";
import { atom, useRecoilState } from "recoil";
import { Instance } from "@popperjs/core/lib/popper";
import FolderChip from "./FolderChip";
import { MirageConstants } from "@shared/mirage/mirageConstants";
interface FolderPickerProps {
    folders?: IFolderPath[];
    allowMultiFolders?: boolean;
    /**
     * The value of the selected folder, if multiple folders are selected, the value should be comma separated
     */
    value?: string | null;
    onFolderSelect?: (newFolders: string) => void;
    readOnly?: boolean;
    label?: string;
    /**
     * If true, the picker will be displayed with higher z-index to overlay dialogs
     */
    dialogMode?: boolean;
    /**
     * @default false
     */
    defaultOpen?: boolean;
}

interface SelectedFolder {
    caption: string;
    superFolder: string;
    folder: string;
}

const folderPickerOpenState = atom({
    key: "folderPickerOpenState",
    default: { [""]: null } as { [x: string]: HTMLElement | null },
});
const FolderPicker: React.FC<FolderPickerProps> = memo((props) => {
    const { folders = [], onFolderSelect, value = "", readOnly, label, dialogMode, allowMultiFolders, defaultOpen } = props;
    const [openState, setCurrentField] = useRecoilState(folderPickerOpenState);
    const [selectedFolders, setSelectedFolders] = useState<SelectedFolder[]>([]);
    const pickerControlRef = useRef<HTMLButtonElement | null>(null);
    const emptyFolder = "(none)";
    const folderPathToSelectedFolder = useCallback((folder: string) => {
        if (folder === emptyFolder) {
            return {
                superFolder: emptyFolder,
                caption: emptyFolder,
                folder: "",
            };
        }
        const [superFolder, subFolder] = folder.split(MirageConstants.FoldersDivider);
        if (!superFolder && !subFolder) {
            console.error("folder is missing superFolder and subFolder", folder);
        }
        return {
            superFolder,
            caption: subFolder,
            folder: folder,
        };
    }, []);
    const foldersUpdater = useCallback(
        (currentFolders: SelectedFolder[], newFolder: SelectedFolder[], deleteIndex?: number) => {
            const copyOfCurrent = [...currentFolders];
            if (typeof deleteIndex === "number") {
                copyOfCurrent.splice(deleteIndex, 1);
                return copyOfCurrent;
            }
            if (allowMultiFolders) {
                const result = [...copyOfCurrent, ...newFolder];
                const uniqueResult = result.filter((item, idx) => {
                    return result.findIndex((i) => i.folder === item.folder) === idx;
                });
                return uniqueResult;
            } else {
                return [...newFolder];
            }
        },
        [allowMultiFolders]
    );
    const updateSelectedFolders = useCallback(
        (newFolder: SelectedFolder[]) => {
            setSelectedFolders((p) => foldersUpdater(p, newFolder));
            if (!onFolderSelect) {
                return;
            }
            const newFolders = foldersUpdater(selectedFolders, newFolder);
            if (newFolders.length > 1) {
                const stringifiedFolders = newFolders.map((folder) => folder.folder).join(",");
                onFolderSelect(stringifiedFolders || "");
            } else {
                onFolderSelect(newFolders[0]?.folder || "");
            }
        },
        [foldersUpdater, onFolderSelect, selectedFolders]
    );
    const folderOnDelete = useCallback(
        (idx: number) => {
            setSelectedFolders((p) => foldersUpdater(p, [], idx));
            if (!onFolderSelect) {
                return;
            }
            const newFolders = foldersUpdater(selectedFolders, [], idx);
            if (newFolders.length > 1) {
                const stringifiedFolders = newFolders.map((folder) => folder.folder).join(",");
                onFolderSelect(stringifiedFolders || "");
            } else {
                onFolderSelect(newFolders[0]?.folder || "");
            }
        },
        [foldersUpdater, onFolderSelect, selectedFolders]
    );

    const popperRef = useRef<Instance | null>(null);

    const id = useId();
    const theme = useTheme();
    const handleClick = useCallback(
        (event: React.MouseEvent<HTMLElement>) => {
            event.stopPropagation();
            setCurrentField({ [id]: event.currentTarget });
        },
        [id, setCurrentField]
    );
    const anchorEl = openState[id] || null;

    useEffect(() => {
        if (value) {
            const comaSeparatedFolders = value?.split(",");
            const selectedFolders = comaSeparatedFolders?.map((folder) => folderPathToSelectedFolder(folder));
            if (selectedFolders) {
                setSelectedFolders(selectedFolders);
            }
        } else {
            setSelectedFolders([]);
        }
    }, [value]);

    useEffect(() => {
        if (defaultOpen && pickerControlRef.current) {
            setCurrentField({ [id]: pickerControlRef.current });
        }
    }, [defaultOpen]);

    const handleClose = useCallback(() => {
        setCurrentField(() => {
            return {};
        });
        if (anchorEl) {
            anchorEl.focus();
        }
    }, [anchorEl, setCurrentField]);
    const handleSelectOption = useCallback(
        (opt: SelectedFolder) => {
            updateSelectedFolders([opt]);
            handleClose();
        },
        [handleClose, updateSelectedFolders]
    );

    const options: SelectedFolder[] = useMemo(() => {
        const copyOfFolders = [...folders];
        !allowMultiFolders && copyOfFolders.unshift(emptyFolder);
        return copyOfFolders.map((option) => folderPathToSelectedFolder(option)).filter(Boolean);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [folders.length, selectedFolders]);
    // below sort fixes grouping by superfolder
    options.sort((a, b) => a.superFolder.localeCompare(b.superFolder));

    const filterOptions = useCallback((options: SelectedFolder[], state: FilterOptionsState<SelectedFolder>) => {
        return options.filter((option) => {
            if (!option.caption && !option.superFolder) {
                console.error("option is missing caption and superFolder", option);
                return false;
            }
            if (option.folder === emptyFolder) {
                return true;
            }
            if (state.inputValue === "") {
                return true;
            }
            const captionMatch = option.caption?.toLowerCase().includes(state.inputValue.toLowerCase());
            const superFolderMatch = option.superFolder.toLowerCase().includes(state.inputValue.toLowerCase());
            return captionMatch || superFolderMatch;
        });
    }, []);
    const onInputChange = useCallback((event: React.SyntheticEvent<Element, Event>) => {
        if ((event as React.KeyboardEvent).key === "Backspace") {
            return;
        }
        if (popperRef.current) {
            popperRef.current.update();
        }
    }, []);
    const onAutocompleteClose = useCallback(
        (event: React.ChangeEvent<any>, reason: AutocompleteCloseReason) => {
            if (reason === "escape") {
                handleClose();
            }
        },
        [handleClose]
    );
    const isOptionEqualToValue = useCallback((o: SelectedFolder, v: SelectedFolder) => o.caption === v.caption, []);
    const renderTags = useCallback(() => null, []);
    const groupBy = useCallback((option: SelectedFolder) => option.superFolder, []);
    const getOptionLabel = useCallback((option: SelectedFolder) => option.caption || option.superFolder || "", []);
    const renderInput = useCallback(
        (params: AutocompleteRenderInputParams) => <StyledInput ref={params.InputProps.ref} inputProps={params.inputProps} autoFocus placeholder="Filter folders" />,
        []
    );

    const renderGroup = useCallback(
        (params: AutocompleteRenderGroupParams) => {
            if (!params.group) {
                return null;
            }
            return (
                <li key={params.key}>
                    <CustomGroup handleSelectOption={handleSelectOption} groupName={params.group}>
                        {params.children}
                    </CustomGroup>
                </li>
            );
        },
        [handleSelectOption]
    );
    const renderOption = useCallback(
        (_props: React.HTMLAttributes<HTMLLIElement>, option: SelectedFolder) => {
            if (!option.caption) {
                return null;
            }
            return (
                <Box
                    key={option.folder}
                    sx={{
                        padding: "6px",
                        paddingLeft: "15px",
                        ":hover": {
                            backgroundColor: theme.palette.action.hover,
                            color: theme.palette.text.primary,
                            cursor: "pointer",
                        },
                    }}
                    onClick={() => handleSelectOption(option)}
                >
                    {option.caption}
                </Box>
            );
        },
        [handleSelectOption, theme.palette.action.hover, theme.palette.text.primary]
    );
    const open = Boolean(openState[id]);
    const styledPopperSx = useMemo(() => {
        return {
            zIndex: dialogMode ? 9999 : 1200,
        };
    }, [dialogMode]);
    const styledPopperModifiers = useMemo(() => {
        return [
            {
                name: "flip",
                enabled: true,
                options: {
                    rootBoundary: "document",
                    padding: 8,
                },
            },
            {
                name: "preventOverflow",
                enabled: true,
                options: {
                    rootBoundary: "document",
                    padding: 8,
                },
            },
        ];
    }, []);
    const selectFolderWrapperSx = useMemo(() => {
        return {
            borderBottom: `1px solid ${theme.palette.mode === "light" ? "#eaecef" : "#30363d"}`,
            padding: "8px 10px",
            fontWeight: 600,
        };
    }, [theme.palette.mode]);

    const editablePicker = (
        <>
            <Box>
                <EditablePickerValue
                    onDelete={folderOnDelete}
                    pickerControlRef={pickerControlRef}
                    selectedFolders={selectedFolders}
                    handleClick={handleClick}
                    label={label}
                    readOnly={readOnly}
                    allowMultiFolders={allowMultiFolders}
                />
            </Box>
            <StyledPopper id={id} open={open} anchorEl={anchorEl} placement="bottom-start" sx={styledPopperSx} popperRef={popperRef} modifiers={styledPopperModifiers}>
                <ClickAwayListener onClickAway={handleClose}>
                    <div>
                        <Box sx={selectFolderWrapperSx}>Select folder</Box>
                        <Autocomplete
                            open
                            filterOptions={filterOptions}
                            isOptionEqualToValue={isOptionEqualToValue}
                            onClose={onAutocompleteClose}
                            onInputChange={onInputChange}
                            PopperComponent={PopperComponent}
                            renderTags={renderTags}
                            renderOption={renderOption}
                            options={options}
                            groupBy={groupBy}
                            getOptionLabel={getOptionLabel}
                            renderInput={renderInput}
                            renderGroup={renderGroup}
                        />
                    </div>
                </ClickAwayListener>
            </StyledPopper>
        </>
    );

    return <Box>{readOnly ? <ReadonlyPicker allowMultiFolders={allowMultiFolders} selectedFolders={selectedFolders} label={label} readOnly={readOnly} /> : editablePicker}</Box>;
});

function folderValueParser(value?: SelectedFolder[]) {
    if (!value?.length) {
        return "[...]";
    }
    return value?.map(({ folder }) => {
        if (!folder) {
            return "[...]";
        }
        return folder;
    });
}

const CustomGroup: React.FC<CustomFolderPickerGroup> = ({ children, groupName, handleSelectOption }) => {
    const headerStyle = {
        position: "sticky",
        zIndex: 1000,
        top: "-1px",
        padding: "10px",
        color: MirageTheme.palette.primary.main,
        backgroundColor: lighten(MirageTheme.palette.primary.light, 0.85),
        ":hover": {
            backgroundColor: lighten(MirageTheme.palette.primary.light, 0.65),

            color: MirageTheme.palette.text.primary,
            cursor: "pointer",
        },
    };
    return (
        <>
            <Box sx={headerStyle} onClick={() => handleSelectOption({ folder: groupName, caption: groupName, superFolder: "" })}>
                {groupName}
            </Box>
            {groupName !== "(none)" ? <GroupItems>{children}</GroupItems> : null}
        </>
    );
};

const GroupItems = styled("ul")({
    padding: 0,
    paddingLeft: 1,
});

const StyledInput = styled(InputBase)(({ theme }) => ({
    padding: 10,
    width: "100%",
    borderBottom: `1px solid ${theme.palette.mode === "light" ? "#eaecef" : "#30363d"}`,
    "& input": {
        borderRadius: 4,
        backgroundColor: theme.palette.mode === "light" ? "#fff" : "#0d1117",
        padding: 8,
        transition: theme.transitions.create(["border-color", "box-shadow"]),
        border: `1px solid ${theme.palette.mode === "light" ? "#eaecef" : "#30363d"}`,
        fontSize: 14,
        "&:focus": {
            boxShadow: `0px 0px 0px 3px ${theme.palette.mode === "light" ? "rgba(3, 102, 214, 0.3)" : "rgb(12, 45, 107)"}`,
            borderColor: theme.palette.mode === "light" ? "#0366d6" : "#388bfd",
        },
    },
}));

const StyledAutocompletePopper = styled("div")(({ theme }) => ({
    [`& .${autocompleteClasses.paper}`]: {
        boxShadow: "none",
        margin: 0,
        color: "inherit",
        fontSize: 13,
    },
    [`& .${autocompleteClasses.listbox}`]: {
        backgroundColor: theme.palette.mode === "light" ? "#fff" : "#1c2128",
        padding: 0,
        [`& .${autocompleteClasses.option}`]: {
            minHeight: "auto",
            alignItems: "flex-start",
            padding: 8,
            borderBottom: `1px solid  ${theme.palette.mode === "light" ? " #eaecef" : "#30363d"}`,
            '&[aria-selected="true"]': {
                backgroundColor: "transparent",
            },
            [`&.${autocompleteClasses.focused}, &.${autocompleteClasses.focused}[aria-selected="true"]`]: {
                backgroundColor: theme.palette.action.hover,
            },
        },
    },
    [`&.${autocompleteClasses.popperDisablePortal}`]: {
        position: "relative",
    },
}));

const PopperComponent = (props: PopperComponentProps) => {
    const { disablePortal, anchorEl, open, ...other } = props;
    return <StyledAutocompletePopper {...other} />;
};

const StyledPopper = styled(Popper)(({ theme }) => ({
    border: `1px solid ${theme.palette.mode === "light" ? "#e1e4e8" : "#30363d"}`,
    boxShadow: `0 8px 24px ${theme.palette.mode === "light" ? "rgba(149, 157, 165, 0.2)" : "rgb(1, 4, 9)"}`,
    borderRadius: 6,
    width: 300,
    zIndex: theme.zIndex.modal,
    fontSize: 13,
    color: theme.palette.mode === "light" ? "#24292e" : "#c9d1d9",
    backgroundColor: theme.palette.mode === "light" ? "#fff" : "#1c2128",
}));

const MultipleFolders = memo(({ selectedFolders, children, onDelete }: MultipleFoldersProps) => {
    const id = useId();
    const getFolderDelete = useCallback(
        (idx: number) => {
            if (onDelete) {
                return () => onDelete(idx);
            }
            return undefined;
        },
        [onDelete]
    );
    return (
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
            {selectedFolders.map((folder, idx) => {
                const folderDelete = getFolderDelete(idx);
                return <FolderChip key={`${id}-${idx}`} folderName={folder.folder} onDelete={folderDelete} />;
            })}
            {children}
        </div>
    );
});

const ReadonlyPicker = memo(({ label, readOnly, selectedFolders, allowMultiFolders }: ReadonlyPickerProps) => {
    const multiFoldersComponent = <MultipleFolders selectedFolders={selectedFolders} />;
    if (!label) {
        if (allowMultiFolders) {
            return multiFoldersComponent;
        }
        return <Typography variant="body2">{folderValueParser(selectedFolders)}</Typography>;
    }
    if (allowMultiFolders) {
        return (
            <MirageFormControl label={label} mode={readOnly ? "view" : "edit"}>
                <Box>
                    {multiFoldersComponent}
                    {!selectedFolders.length ? <Typography variant="body2">[...]</Typography> : null}
                </Box>
            </MirageFormControl>
        );
    }
    return (
        <MirageFormControl label={label} mode={readOnly ? "view" : "edit"}>
            <Typography variant="body2">{folderValueParser(selectedFolders)}</Typography>
        </MirageFormControl>
    );
});

const EditablePickerValue = memo(({ label, handleClick, readOnly, selectedFolders, pickerControlRef, onDelete, allowMultiFolders }: EditablePickerProps) => {
    const onFolderDelete = useCallback(
        (idx: number) => {
            onDelete(idx);
        },
        [onDelete]
    );
    const multiFoldersComponent = (
        <MultipleFolders selectedFolders={selectedFolders} onDelete={onFolderDelete}>
            <Link component="button" ref={pickerControlRef} onClick={handleClick} variant="body2" underline="hover">
                <span>[...]</span>
            </Link>
        </MultipleFolders>
    );
    if (!label) {
        if (allowMultiFolders) {
            return multiFoldersComponent;
        }
        return (
            <Link component="button" ref={pickerControlRef} onClick={handleClick} variant="body2" underline="hover">
                <span>{folderValueParser(selectedFolders)}</span>
            </Link>
        );
    }

    if (allowMultiFolders) {
        return (
            <MirageFormControl label={label} mode={readOnly ? "view" : "edit"}>
                {multiFoldersComponent}
            </MirageFormControl>
        );
    }

    return (
        <MirageFormControl label={label} mode={readOnly ? "view" : "edit"}>
            <Link component="button" ref={pickerControlRef} onClick={handleClick} variant="body2" underline="hover">
                <span>{folderValueParser(selectedFolders)}</span>
            </Link>
        </MirageFormControl>
    );
});

interface MultipleFoldersProps {
    selectedFolders: SelectedFolder[];
    onDelete?: (idx: number) => void;
    children?: React.ReactNode;
}

interface EditablePickerProps {
    selectedFolders: SelectedFolder[];
    handleClick: (event: React.MouseEvent<HTMLElement>) => void;
    onDelete: (idx: number) => void;
    pickerControlRef: React.MutableRefObject<HTMLButtonElement | null>;
    allowMultiFolders?: boolean;
    label?: string;
    readOnly?: boolean;
}

interface ReadonlyPickerProps {
    selectedFolders: SelectedFolder[];
    allowMultiFolders?: boolean;
    label?: string;
    readOnly?: boolean;
}

interface PopperComponentProps {
    anchorEl?: any;
    disablePortal?: boolean;
    open: boolean;
}

interface CustomFolderPickerGroup {
    children?: React.ReactNode;
    groupName: string;
    handleSelectOption: (opt: SelectedFolder) => void;
}

export default FolderPicker;
